import { useQuery } from '@tanstack/react-query';
import type { SalesOrderStatus } from 'scheduler-types';
import { apiUrl, mockAuthHeaders } from '../lib/api';

// Line item type for order detail
export interface OrderLineItem {
  id: string;
  partNumber: string;
  quantity: number;
  bomId: string | null;
  schedulingStatus: string;
  createdAt: string;
  updatedAt: string;
}

// Full order with line items
export interface OrderDetail {
  id: string;
  gpSalesOrderNumber: string;
  customerId: string;
  customerName: string;
  promisedShipDate: string;
  expectedShipDate: string | null;
  status: SalesOrderStatus;
  priority: string;
  createdAt: string;
  updatedAt: string;
  manufacturingOrderId: string | null;
  lineItems: OrderLineItem[];
}

async function fetchOrder(id: string): Promise<OrderDetail> {
  const response = await fetch(apiUrl(`/api/v1/orders/${id}`), {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: { message: response.statusText },
    }));
    throw new Error(error.error?.message ?? `Failed to fetch order: ${response.status}`);
  }

  const json = await response.json();
  const data = json.data;

  // Defensive check - ensure data exists
  if (!data || typeof data !== 'object') {
    console.error('[useOrder] Expected object but got:', typeof data, data);
    throw new Error('Invalid order data received');
  }

  // Transform API response to match frontend's expected shape
  // API returns manufacturingOrders with nested lineItems, but frontend expects flat lineItems
  const manufacturingOrders = data.manufacturingOrders || [];
  const firstMo = manufacturingOrders[0];
  const lineItems = firstMo?.lineItems || [];

  // Get dates from first line item if available (order-level dates come from line items)
  const firstLineItem = lineItems[0];

  return {
    id: data.id,
    gpSalesOrderNumber: data.gpSalesOrderNumber,
    customerId: data.customerId,
    customerName: data.customerName,
    promisedShipDate: firstLineItem?.promisedShipDate || '',
    expectedShipDate: firstLineItem?.expectedShipDate || null,
    status: data.status,
    priority: data.priority,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    manufacturingOrderId: firstMo?.id || null,
    lineItems: lineItems.map((li: {
      id: string;
      partNumber: string;
      quantity: number;
      bomId: string | null;
      schedulingStatus: string;
    }) => ({
      id: li.id,
      partNumber: li.partNumber,
      quantity: li.quantity,
      bomId: li.bomId,
      schedulingStatus: li.schedulingStatus,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    })),
  };
}

export function useOrder(id: string | undefined) {
  return useQuery({
    queryKey: ['order', id],
    queryFn: () => fetchOrder(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}
